// Centralized mapping between the backend's `TableFeature` enum (source of
// truth, stored on `Table.features` and accepted by
// `suggest-tables?features=`) and the Spanish labels shown in the UI.
// Anything that needs to *display* a feature should go through
// `tableFeatureLabel`; anything that stores or sends one should use the
// enum value.

import type { ApiTableFeature } from "@/types/api";
import { Accessibility, Dog, type LucideIcon } from "lucide-react";

export interface TableFeatureOption {
  value: ApiTableFeature;
  label: string;
}

export const TABLE_FEATURES: readonly TableFeatureOption[] = [
  { value: "accessible", label: "Mesa accesible" },
  { value: "pet_friendly", label: "Mascota" },
  { value: "high_chair", label: "Silla de bebé" },
  { value: "outdoor_view", label: "Vista exterior" },
  { value: "quiet_zone", label: "Zona tranquila" },
] as const;

// Subset shown in operator-facing pickers. Mirrors the public guest
// site (reservas/) which only exposes "mascota" and "accesible" — the
// remaining backend features stay valid on Table.features and admin
// CRUD, but staff doesn't toggle them from the reservation UIs so the
// two surfaces stay aligned. ``tableFeatureLabel`` still resolves the
// labels of the wider set when a reservation already carries them.
export const PICKABLE_TABLE_FEATURES: readonly TableFeatureOption[] = TABLE_FEATURES.filter(
  (f) => f.value === "pet_friendly" || f.value === "accessible",
);

const LABEL_BY_VALUE: Record<ApiTableFeature, string> = TABLE_FEATURES.reduce(
  (acc, { value, label }) => {
    acc[value] = label;
    return acc;
  },
  {} as Record<ApiTableFeature, string>,
);

export function tableFeatureLabel(value: ApiTableFeature): string {
  return LABEL_BY_VALUE[value] ?? value;
}

// Lucide icon components for the two pickable features used in compact
// cards and modal chips across the staff SPA.
export const FEATURE_ICONS: Partial<Record<ApiTableFeature, LucideIcon>> = {
  pet_friendly: Dog,
  accessible: Accessibility,
};
